import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  Plus,
  MoreHorizontal,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  FolderKanban,
  Pencil,
  Archive,
  BarChart3,
  CircleDollarSign,
  BriefcaseBusiness,
  Gauge,
  RotateCcw,
  Trash2
} from 'lucide-react'
import { supabase } from '../supabase'
import { useToast } from '../Toast'
import { useUser } from '../UserContext'
import { withOrg } from '../lib/withOrg'
import {
  getCompletedDeliveryCount,
  getDeliveryProgress,
  getProgressLabel,
  getTimelineStatus
} from '../lib/healthStatus'
import ConfirmModal from '../ConfirmModal'
import { sumInCurrency } from '../lib/currencyConversion'
import { REPORTING_CURRENCIES, useReportingCurrency } from '../lib/useReportingCurrency'

const EMPTY_FORM = {
  name: '',
  portfolio_id: '',
  status: 'active',
  start_date: '',
  end_date: '',
  notes: '',
  assigned_to: '',
  business_unit_id: '',
}

const EMPTY_PORTFOLIO_FORM = {
  name: '',
  description: '',
  business_unit_id: ''
}

const UNASSIGNED_PORTFOLIO_ID = '__unassigned__'

function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

function currencySymbol(c) {
  if (c === 'GBP') return '£'
  if (c === 'EUR') return '€'
  if (c === 'AUD') return 'A$'
  if (c === 'CAD') return 'CA$'
  return '$'
}

function formatMoney(value, currency = 'GBP') {
  const amount = Number(value || 0)
  const symbol = currencySymbol(currency)

  if (amount >= 1000000) return `${symbol}${(amount / 1000000).toFixed(2)}M`
  if (amount >= 1000) return `${symbol}${Math.round(amount / 1000)}k`

  return `${symbol}${amount.toLocaleString()}`
}

function groupAmountsByCurrency(items, amountKey = 'amount') {
  return items.reduce((acc, item) => {
    const currency = item.currency || 'GBP'
    acc[currency] = (acc[currency] || 0) + (Number(item[amountKey]) || 0)
    return acc
  }, {})
}

function formatCurrencyTotals(totals) {
  const entries = Object.entries(totals).filter(([, value]) => Number(value) > 0)

  if (entries.length === 0) return '—'

  return entries
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, value]) => formatMoney(value, currency))
    .join(' · ')
}

function getPrimaryRevenueValue(totals) {
  return Object.values(totals).reduce((sum, value) => sum + Number(value || 0), 0)
}

function getReportingRevenue(totals, reportingCurrency, rateSnapshot) {
  const items = Object.entries(totals).filter(([, value]) => Number(value) > 0).map(([currency, value]) => ({ currency, value }))
  if (!items.length) return 0
  if (!rateSnapshot?.rates) return null
  return sumInCurrency(items, reportingCurrency, rateSnapshot.rates)
}

function formatReportingRevenue(totals, reportingCurrency, rateSnapshot) {
  if (!Object.values(totals).some(value => Number(value) > 0)) return '—'
  const converted = getReportingRevenue(totals, reportingCurrency, rateSnapshot)
  return converted === null ? formatCurrencyTotals(totals) : formatMoney(converted, reportingCurrency)
}

function formatStatus(status) {
  if (!status) return 'Ongoing'
  return status
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}


export default function Projects() {
  const toast = useToast()
  const navigate = useNavigate()
  const { orgId, can, businessUnits, activeBusinessUnitId } = useUser()
  const { reportingCurrency, rateSnapshot, changeReportingCurrency } = useReportingCurrency(orgId)
  const canViewFinancials = can('financials.view')
  const canManageProjects = can('projects.manage')

  const [projects, setProjects] = useState([])
  const [portfolios, setPortfolios] = useState([])
  const [allocations, setAllocations] = useState([])
  const [rights, setRights] = useState([])
  const [deliveries, setDeliveries] = useState([])
  const [profiles, setProfiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [showPortfolioForm, setShowPortfolioForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [portfolioSaving, setPortfolioSaving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [confirmPortfolioDelete, setConfirmPortfolioDelete] = useState(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('active')
  const [sortBy, setSortBy] = useState('revenue_desc')
  const [form, setForm] = useState(EMPTY_FORM)
  const [portfolioForm, setPortfolioForm] = useState(EMPTY_PORTFOLIO_FORM)
  const [editingProjectId, setEditingProjectId] = useState(null)
  const [editingPortfolioId, setEditingPortfolioId] = useState(null)
  const [collapsedPortfolios, setCollapsedPortfolios] = useState({})
  const [activePortfolioMenuId, setActivePortfolioMenuId] = useState(null)
  const [activeProjectMenuId, setActiveProjectMenuId] = useState(null)
  const [draggingProjectId, setDraggingProjectId] = useState(null)
  const [dragOverPortfolioId, setDragOverPortfolioId] = useState(null)

  useEffect(() => {
    fetchAll()
    fetchProfiles()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBusinessUnitId, orgId])

  useEffect(() => {
    if (!activePortfolioMenuId && !activeProjectMenuId) return undefined

    function closeMenusOnOutsideClick(event) {
      if (event.target.closest('[data-project-menu]')) return

      setActivePortfolioMenuId(null)
      setActiveProjectMenuId(null)
    }

    document.addEventListener('pointerdown', closeMenusOnOutsideClick)

    return () => {
      document.removeEventListener('pointerdown', closeMenusOnOutsideClick)
    }
  }, [activePortfolioMenuId, activeProjectMenuId])

  async function fetchAll() {
    setError(null)
    setLoading(true)

    const [
      { data: portfolioData, error: portfolioError },
      { data: projectsData, error: projectError },
      { data: allocationData, error: allocationError },
      { data: rightsData, error: rightsError },
      { data: deliveryData, error: deliveryError }
    ] = await Promise.all([
      supabase
        .from('portfolios')
        .select('*')
        .eq('org_id', orgId)
        .order('name'),

      supabase
        .from('secure_projects')
        .select('*')
        .eq('org_id', orgId)
        .order('created_at', { ascending: false }),

      supabase
        .from('secure_deal_project_allocations')
        .select('*')
        .eq('org_id', orgId),

      supabase
        .from('rights')
        .select('id, project_id, deal_id, quantity')
        .eq('org_id', orgId),

      supabase
        .from('deliveries')
        .select('id, right_id, status, delivery_date, custom_fields')
        .eq('org_id', orgId)
    ])

    if (portfolioError) {
      console.error('Failed to load portfolios:', portfolioError)
      setError(`Failed to load portfolios. Make sure the portfolios migration has been applied. ${portfolioError.message}`)
      setLoading(false)
      return
    }

    if (projectError) {
      console.error('Failed to load projects:', projectError)
      setError('Failed to load projects.')
      setLoading(false)
      return
    }

    if (allocationError) {
      console.error('Failed to load allocations:', allocationError)
    }

    if (rightsError) {
      console.error('Failed to load rights:', rightsError)
    }

    if (deliveryError) {
      console.error('Failed to load deliveries:', deliveryError)
    }

    setPortfolios((portfolioData || []).filter(item => !activeBusinessUnitId || item.business_unit_id === activeBusinessUnitId))
    setProjects((projectsData || []).filter(item => !activeBusinessUnitId || item.business_unit_id === activeBusinessUnitId))
    setAllocations(allocationData || [])
    setRights(rightsData || [])
    setDeliveries(deliveryData || [])
    setLoading(false)
  }

  async function fetchProfiles() {
    const { data } = await supabase
      .from('profiles')
      .select('id, email, full_name')
      .order('full_name')

    setProfiles(data || [])
  }

  function startNewProject() {
    setForm({ ...EMPTY_FORM, business_unit_id: activeBusinessUnitId || businessUnits[0]?.id || '' })
    setEditingProjectId(null)
    setShowForm(prev => !prev)
  }

  function startNewPortfolio() {
    if (showPortfolioForm && !editingPortfolioId) {
      setShowPortfolioForm(false)
      return
    }

    setPortfolioForm({ ...EMPTY_PORTFOLIO_FORM, business_unit_id: activeBusinessUnitId || businessUnits[0]?.id || '' })
    setEditingPortfolioId(null)
    setShowPortfolioForm(true)
  }

  function editProject(project) {
    setForm({
      name: project.name || '',
      portfolio_id: project.portfolio_id || '',
      status: project.status || 'active',
      start_date: project.start_date || '',
      end_date: project.end_date || '',
      notes: project.notes || '',
      assigned_to: project.assigned_to || '',
      business_unit_id: project.business_unit_id || ''
    })
    setEditingProjectId(project.id)
    setShowForm(true)
    setActiveProjectMenuId(null)
  }

  function resetProjectForm() {
    setForm(EMPTY_FORM)
    setEditingProjectId(null)
    setShowForm(false)
  }

  async function saveProject() {
    if (!form.name.trim()) {
      toast.error('Project name is required')
      return
    }
    if (!form.business_unit_id) return toast.error('Business is required')

    setSaving(true)

    const payload = {
      ...form,
      portfolio_id: form.portfolio_id || null,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      assigned_to: form.assigned_to || null,
    }

    const result = editingProjectId
      ? await supabase
        .from('projects')
        .update(payload)
        .eq('id', editingProjectId)
        .eq('org_id', orgId)
      : await supabase
        .from('projects')
        .insert([withOrg(payload, orgId)])

    setSaving(false)

    if (result.error) {
      console.error('Failed to save project:', result.error)
      toast.error(`Failed to save project: ${result.error.message}`)
      return
    }

    toast.success(editingProjectId ? 'Project updated' : 'Project created')
    resetProjectForm()
    fetchAll()
  }

  async function savePortfolio() {
    if (!portfolioForm.name.trim()) {
      toast.error('Portfolio name is required')
      return
    }
    if (!portfolioForm.business_unit_id) return toast.error('Business is required')

    setPortfolioSaving(true)

    const payload = {
      name: portfolioForm.name.trim(),
      description: portfolioForm.description.trim() || null,
      business_unit_id: portfolioForm.business_unit_id || null
    }

    const result = editingPortfolioId
      ? await supabase
        .from('portfolios')
        .update(payload)
        .eq('id', editingPortfolioId)
        .eq('org_id', orgId)
      : await supabase
        .from('portfolios')
        .insert([withOrg({ ...payload, status: 'active' }, orgId)])

    setPortfolioSaving(false)

    if (result.error) {
      console.error('Failed to save portfolio:', result.error)
      toast.error(`Failed to save portfolio: ${result.error.message}`)
      return
    }

    toast.success(editingPortfolioId ? 'Portfolio updated' : 'Portfolio created')
    resetPortfolioForm()
    fetchAll()
  }

  function editPortfolio(portfolio) {
    setPortfolioForm({
      name: portfolio.name || '',
      description: portfolio.description || '',
      business_unit_id: portfolio.business_unit_id || ''
    })
    setEditingPortfolioId(portfolio.id)
    setShowPortfolioForm(true)
  }

  function resetPortfolioForm() {
    setPortfolioForm(EMPTY_PORTFOLIO_FORM)
    setEditingPortfolioId(null)
    setShowPortfolioForm(false)
  }

  async function updatePortfolioStatus(portfolioId, status) {
    const { error: updateError } = await supabase
      .from('portfolios')
      .update({ status })
      .eq('id', portfolioId)
      .eq('org_id', orgId)

    if (updateError) {
      console.error('Failed to update portfolio:', updateError)
      toast.error(`Failed to update portfolio: ${updateError.message}`)
      return
    }

    toast.success(status === 'archived' ? 'Portfolio archived' : 'Portfolio restored')
    fetchAll()
  }

  async function deletePortfolio(portfolioId) {
    const projectCount = projects.filter(project => project.portfolio_id === portfolioId).length

    if (projectCount > 0) {
      toast.error('Only empty portfolios can be deleted')
      setConfirmPortfolioDelete(null)
      return
    }

    const { error: deleteError } = await supabase
      .from('portfolios')
      .delete()
      .eq('id', portfolioId)
      .eq('org_id', orgId)

    if (deleteError) {
      console.error('Failed to delete portfolio:', deleteError)
      toast.error(`Failed to delete portfolio: ${deleteError.message}`)
      return
    }

    toast.success('Portfolio deleted')
    setConfirmPortfolioDelete(null)
    fetchAll()
  }

  function togglePortfolio(portfolioId) {
    setCollapsedPortfolios(prev => ({
      ...prev,
      [portfolioId]: !prev[portfolioId]
    }))
  }

  async function deleteProject(id) {
    const { error } = await supabase.rpc('delete_project', { target_project_id: id })

    if (error) {
      console.error('Failed to delete project:', error)
      toast.error(`Failed to delete project: ${error.message}`)
    } else {
      toast.success('Project deleted')
    }

    setConfirmDelete(null)
    fetchAll()
  }

  async function moveProjectToPortfolio(projectId, portfolioId) {
    const nextPortfolioId = portfolioId === UNASSIGNED_PORTFOLIO_ID ? null : portfolioId
    const project = projects.find(item => item.id === projectId)

    if (!project || (project.portfolio_id || null) === nextPortfolioId) return

    const previousProjects = projects

    setProjects(currentProjects =>
      currentProjects.map(item =>
        item.id === projectId
          ? {
              ...item,
              portfolio_id: nextPortfolioId,
              portfolios: nextPortfolioId
                ? portfolios.find(portfolio => portfolio.id === nextPortfolioId) || item.portfolios
                : null
            }
          : item
      )
    )

    const { error: updateError } = await supabase
      .from('projects')
      .update({ portfolio_id: nextPortfolioId })
      .eq('id', projectId)
      .eq('org_id', orgId)

    if (updateError) {
      console.error('Failed to move project:', updateError)
      setProjects(previousProjects)
      toast.error(`Failed to move project: ${updateError.message}`)
      return
    }

    const targetPortfolioName = nextPortfolioId
      ? portfolios.find(portfolio => portfolio.id === nextPortfolioId)?.name || 'portfolio'
      : 'Unassigned'

    toast.success(`Project moved to ${targetPortfolioName}`)
  }

  function handleProjectDrop(event, portfolioId) {
    event.preventDefault()
    const projectId = event.dataTransfer.getData('text/plain') || draggingProjectId

    setDragOverPortfolioId(null)
    setDraggingProjectId(null)

    if (!projectId) return

    moveProjectToPortfolio(projectId, portfolioId)
  }

  function getProjectRevenue(projectId) {
    return groupAmountsByCurrency(
      allocations.filter(a => a.project_id === projectId)
    )
  }

  function getProjectDeals(projectId) {
    return allocations.filter(a => a.project_id === projectId)
  }

  const projectCards = useMemo(() => {
    return projects.map(project => {
      const projectAllocations = getProjectDeals(project.id)
      const revenueByCurrency = getProjectRevenue(project.id)
      const convertedRevenue = getReportingRevenue(revenueByCurrency, reportingCurrency, rateSnapshot)
      const revenue = convertedRevenue === null ? getPrimaryRevenueValue(revenueByCurrency) : convertedRevenue
      const activeDeals = projectAllocations.filter(a => a.deals?.status === 'active').length
      const projectRights = rights.filter(r => r.project_id === project.id)
      const projectRightIds = projectRights.map(r => r.id)
      const projectDeliveries = deliveries.filter(d => projectRightIds.includes(d.right_id))
      const completedDeliveries = getCompletedDeliveryCount(projectDeliveries)
      const totalDeliveries = projectDeliveries.length
      const progressScore = getDeliveryProgress(projectDeliveries, project)

      return {
        ...project,
        revenue,
        revenueByCurrency,
        revenueDisplay: formatReportingRevenue(revenueByCurrency, reportingCurrency, rateSnapshot),
        dealCount: projectAllocations.length,
        activeDeals,
        rightCount: projectRights.length,
        deliveryCount: totalDeliveries,
        completedDeliveries,
        healthScore: progressScore,
        progressLabel: getProgressLabel(progressScore),
        statusBadge: getTimelineStatus(project, projectDeliveries),
        nextDeadline: project.end_date || project.start_date || null
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, allocations, rights, deliveries, reportingCurrency, rateSnapshot])

  const filtered = projectCards
    .filter(project =>
      (
        (project.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (project.notes || '').toLowerCase().includes(search.toLowerCase()) ||
        (project.portfolios?.name || '').toLowerCase().includes(search.toLowerCase())
      ) &&
      (
        statusFilter === 'all' ||
        (statusFilter === 'active' && project.status !== 'archived') ||
        project.status === statusFilter
      )
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'name_asc':
          return (a.name || '').localeCompare(b.name || '')

        case 'name_desc':
          return (b.name || '').localeCompare(a.name || '')

        case 'health_desc':
          return b.healthScore - a.healthScore

        case 'health_asc':
          return a.healthScore - b.healthScore

        case 'revenue_asc':
          return a.revenue - b.revenue

        case 'deadline_asc':
          return new Date(a.nextDeadline || '9999-12-31') - new Date(b.nextDeadline || '9999-12-31')

        case 'revenue_desc':
        default:
          return b.revenue - a.revenue
      }
    })

  const totalRevenueByCurrency = projectCards.reduce((totals, project) => {
    Object.entries(project.revenueByCurrency || {}).forEach(([currency, amount]) => {
      totals[currency] = (totals[currency] || 0) + Number(amount || 0)
    })

    return totals
  }, {})
  const totalActiveDeals = projectCards.reduce((sum, project) => sum + project.activeDeals, 0)
  const totalDeliveries = projectCards.reduce((sum, project) => sum + project.deliveryCount, 0)
  const totalCompletedDeliveries = projectCards.reduce((sum, project) => sum + project.completedDeliveries, 0)
  const avgHealth = totalDeliveries > 0
    ? Math.round((totalCompletedDeliveries / totalDeliveries) * 100)
    : 0

  const projectToDelete = confirmDelete ? projects.find(p => p.id === confirmDelete) : null
  const portfolioToDelete = confirmPortfolioDelete ? portfolios.find(p => p.id === confirmPortfolioDelete) : null

  const portfolioGroups = useMemo(() => {
    const groups = portfolios.map(portfolio => {
      const groupProjects = filtered.filter(project => project.portfolio_id === portfolio.id)

      return buildPortfolioGroup(portfolio, groupProjects, reportingCurrency, rateSnapshot)
    })

    const unassignedProjects = filtered.filter(project => !project.portfolio_id)

    if (unassignedProjects.length || statusFilter !== 'archived') {
      groups.push(buildPortfolioGroup({
        id: UNASSIGNED_PORTFOLIO_ID,
        name: 'Unassigned',
        description: 'Projects not yet allocated to a portfolio.',
        status: 'active',
        system: true
      }, unassignedProjects, reportingCurrency, rateSnapshot))
    }

    return groups.filter(group => {
      if (group.projects.length > 0) return true
      if (group.portfolio.system) return false
      if (search.trim()) return false
      if (statusFilter === 'all') return true
      if (statusFilter === 'archived') return group.portfolio.status === 'archived'
      return group.portfolio.status !== 'archived'
    })
  }, [filtered, portfolios, search, statusFilter, reportingCurrency, rateSnapshot])

  return (
    <div className="hv-page">
      <style>{`
        .projects-topbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;
          margin-bottom: 26px;
        }

        .projects-actions {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: nowrap;
          justify-content: flex-end;
          flex: 1;
          min-width: 0;
        }

        .projects-search {
          position: relative;
          width: 280px;
        }

        .projects-select {
          height: 36px;
          min-width: 154px;
          max-width: 190px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .projects-select--sort {
          width: 196px;
        }

        .projects-create-button {
          height: 36px;
          min-width: 140px;
          padding: 0 14px;
        }

        .projects-reporting-bar { display: flex; align-items: center; justify-content: flex-end; gap: 10px; margin: -8px 0 10px; color: var(--hv-text-muted); font-size: 11px; }
        .projects-reporting-bar label { font-weight: 700; }
        .projects-reporting-bar .hv-select { width: 92px; height: 32px; min-height: 32px; font-size: 11px; }
        .projects-rate-note { margin: -8px 0 18px; text-align: right; color: var(--hv-text-muted); font-size: 10.5px; }

        .summary-strip {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
          gap: 12px;
          margin-bottom: 18px;
        }

        .summary-item {
          display: flex;
          min-width: 0;
          align-items: center;
          gap: 14px;
          padding: 17px 18px;
          border: 1px solid var(--hv-border);
          border-radius: 8px;
          background: #fff;
          box-shadow: var(--hv-shadow-sm);
        }

        .summary-icon { display: inline-flex; width: 44px; height: 44px; flex: 0 0 44px; align-items: center; justify-content: center; border-radius: 50%; }
        .summary-icon.indigo { color: #4F46E5; background: #EEF2FF; }
        .summary-icon.green { color: #059669; background: #E8F8F2; }
        .summary-icon.blue { color: #2563EB; background: #EFF6FF; }
        .summary-icon.amber { color: #D97706; background: #FFF7E6; }
        .summary-copy { min-width: 0; flex: 1; }
        .summary-arrow { flex: 0 0 auto; color: #91A0BC; }

        .summary-value {
          font-size: 22px;
          font-weight: 800;
          color: var(--hv-text-primary);
          letter-spacing: -0.6px;
          line-height: 1;
          margin-bottom: 6px;
        }

        .summary-label {
          font-size: 11px;
          font-weight: 700;
          color: var(--hv-text-muted);
        }

        .projects-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 16px;
        }

        .portfolio-stack {
          display: flex;
          flex-direction: column;
          gap: 18px;
        }

        .portfolio-section {
          background: #fff;
          border: 1px solid var(--hv-border);
          border-radius: 14px;
          box-shadow: 0 1px 3px rgba(15, 21, 35, 0.05);
          overflow: visible;
          transition: border-color 0.16s ease, box-shadow 0.16s ease, background 0.16s ease;
        }

        .portfolio-section--drop-target {
          border-color: #A5B4FC;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12), 0 16px 40px rgba(15, 23, 42, 0.08);
          background: #FAFBFF;
        }

        .portfolio-section-header {
          width: 100%;
          border: none;
          background: #fff;
          padding: 18px 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;
          text-align: left;
        }

        .portfolio-title-block {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }

        .portfolio-icon {
          width: 36px;
          height: 36px;
          border-radius: 11px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #EEF2FF 0%, #F5F3FF 100%);
          color: #4F46E5;
          box-shadow: inset 0 0 0 1px rgba(79, 70, 229, 0.08);
          flex-shrink: 0;
        }

        .portfolio-name {
          font-size: 15px;
          font-weight: 800;
          color: var(--hv-text-primary);
          line-height: 1.2;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .portfolio-description {
          font-size: 12px;
          color: var(--hv-text-muted);
          margin-top: 3px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .portfolio-metrics {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }

        .portfolio-badge {
          height: 28px;
          padding: 0 10px;
          border-radius: 999px;
          background: var(--hv-bg);
          border: 1px solid var(--hv-border-light);
          color: var(--hv-text-secondary);
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-size: 11px;
          font-weight: 800;
          white-space: nowrap;
        }

        .portfolio-actions {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          position: relative;
        }

        .portfolio-action-icon {
          width: 30px;
          height: 30px;
          border-radius: 8px;
          padding: 0;
          color: var(--hv-text-secondary);
          background: #fff;
          border: 1px solid var(--hv-border);
        }

        .portfolio-action-icon:hover {
          background: #F8FAFC;
          color: var(--hv-accent);
          border-color: #D8DEEB;
        }

        .portfolio-dashboard-button {
          height: 30px;
          border-radius: 8px;
          padding: 0 11px;
          font-size: 11.5px;
          font-weight: 800;
          gap: 6px;
          box-shadow: none;
        }

        .portfolio-menu {
          position: absolute;
          right: 0;
          top: calc(100% + 6px);
          width: 190px;
          background: #fff;
          border: 1px solid var(--hv-border);
          border-radius: 10px;
          box-shadow: 0 16px 36px rgba(15, 23, 42, 0.14);
          padding: 6px;
          z-index: 30;
        }

        .portfolio-menu-item {
          width: 100%;
          border: none;
          background: transparent;
          color: var(--hv-text-secondary);
          display: flex;
          align-items: center;
          gap: 9px;
          padding: 9px 10px;
          border-radius: 8px;
          font-size: 12.5px;
          font-weight: 750;
          text-align: left;
          cursor: pointer;
        }

        .portfolio-menu-item:hover {
          background: #F8FAFC;
          color: var(--hv-text-primary);
        }

        .portfolio-menu-item--danger {
          color: #EF4444;
        }

        .portfolio-menu-item--danger:hover {
          background: #FEF2F2;
          color: #DC2626;
        }

        .portfolio-body {
          border-top: 1px solid var(--hv-border-light);
          padding: 16px;
          background: #FBFCFF;
        }

        .portfolio-empty {
          padding: 18px;
          color: var(--hv-text-muted);
          font-size: 13px;
          background: #fff;
          border: 1px dashed var(--hv-border);
          border-radius: 12px;
        }

        .project-card {
          background: #fff;
          border: 1px solid var(--hv-border);
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(15, 21, 35, 0.05);
          position: relative;
          cursor: pointer;
          transition: transform 0.14s ease, box-shadow 0.14s ease, border-color 0.14s ease;
        }

        .project-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 16px 38px rgba(15, 21, 35, 0.09);
          border-color: #D8DEEB;
        }

        .project-card--dragging {
          opacity: 0.55;
          transform: scale(0.985);
          border-color: #A5B4FC;
          box-shadow: 0 18px 42px rgba(79, 70, 229, 0.16);
        }

        .project-card-body {
          padding: 20px;
        }

        .project-card-title-row {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 8px;
        }

        .project-name {
          font-size: 14px;
          font-weight: 800;
          color: var(--hv-text-primary);
          line-height: 1.25;
        }

        .project-date {
          font-size: 11.5px;
          color: var(--hv-text-muted);
          margin-bottom: 28px;
          text-transform: none;
        }

        .project-revenue {
          font-size: 22px;
          font-weight: 800;
          color: var(--hv-text-primary);
          letter-spacing: -0.65px;
          margin-bottom: 4px;
        }

        .project-card-mid {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 14px;
          margin-bottom: 18px;
        }

        .project-mini-label {
          font-size: 11px;
          color: var(--hv-text-muted);
        }

        .health-ring-small {
          width: 62px;
          height: 62px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .health-ring-inner-small {
          width: 46px;
          height: 46px;
          border-radius: 50%;
          background: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          font-weight: 800;
        }

        .project-card-footer {
          border-top: 1px solid var(--hv-border-light);
          padding: 12px 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          color: var(--hv-text-muted);
          font-size: 11px;
        }

        @media (max-width: 1100px) {
          .projects-topbar {
            align-items: flex-start;
            flex-direction: column;
          }

          .projects-actions {
            width: 100%;
            justify-content: flex-start;
            flex-wrap: wrap;
          }

          .projects-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .summary-strip {
            grid-template-columns: repeat(2, 1fr);
            row-gap: 18px;
          }

          .summary-item {
            padding: 17px 18px;
          }
        }
      `}</style>

      <div className="projects-topbar">
        <div>
          <h1 className="hv-page-title">Projects</h1>
          <p className="hv-page-subtitle">
            Portfolios group projects and open into executive reporting dashboards.
          </p>
        </div>

        <div className="projects-actions">
          <div className="projects-search">
            <span className="hv-search-icon">
              <Search size={14} />
            </span>

            <input
              className="hv-search"
              type="text"
              placeholder="Search projects..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <select
            className="hv-select projects-select"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="active">Active portfolios</option>
            <option value="archived">Archived only</option>
            <option value="all">All projects</option>
          </select>

          <select
            className="hv-select projects-select projects-select--sort"
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
          >
            <option value="revenue_desc">Revenue: High to Low</option>
            <option value="revenue_asc">Revenue: Low to High</option>
            <option value="health_desc">Health: High to Low</option>
            <option value="health_asc">Health: Low to High</option>
            <option value="name_asc">Name: A to Z</option>
            <option value="name_desc">Name: Z to A</option>
            <option value="deadline_asc">Next Deadline</option>
          </select>

          {canManageProjects && <button className="hv-btn hv-btn-secondary projects-create-button" onClick={startNewPortfolio}>
            {showPortfolioForm ? 'Cancel' : (
              <>
                <FolderKanban size={15} />
                New Portfolio
              </>
            )}
          </button>}

          {canManageProjects && <button className="hv-btn hv-btn-primary projects-create-button" onClick={startNewProject}>
            {showForm ? 'Cancel' : (
              <>
                <Plus size={15} />
                New Project
              </>
            )}
          </button>}
        </div>
      </div>

      {showPortfolioForm && (
        <div className="hv-card" style={{ padding: '24px', marginBottom: '24px' }}>
          <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--hv-text-primary)', marginBottom: '6px' }}>
            {editingPortfolioId ? 'Rename Portfolio' : 'New Portfolio'}
          </div>

          <p style={{ fontSize: '13px', color: 'var(--hv-text-muted)', marginBottom: '20px' }}>
            Portfolios group related projects and will become executive reporting dashboards.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
                <label className="hv-form-label">Portfolio Name <span style={{ color: 'var(--hv-red)' }}>*</span></label>
              <input
                className="hv-input"
                placeholder="e.g. Esports"
                value={portfolioForm.name}
                onChange={e => setPortfolioForm({ ...portfolioForm, name: e.target.value })}
              />
            </div>

            <div>
              <label className="hv-form-label">Description</label>
              <input
                className="hv-input"
                placeholder="Optional context for this commercial area"
                value={portfolioForm.description}
                onChange={e => setPortfolioForm({ ...portfolioForm, description: e.target.value })}
              />
            </div>

            {businessUnits.length > 1 && <div>
              <label className="hv-form-label">Business <span style={{ color: 'var(--hv-red)' }}>*</span></label>
              <select className="hv-select" value={portfolioForm.business_unit_id} onChange={e => setPortfolioForm({ ...portfolioForm, business_unit_id: e.target.value })}>
                <option value="">Select Business</option>
                {businessUnits.map(unit => <option key={unit.id} value={unit.id}>{unit.name}</option>)}
              </select>
            </div>}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button className="hv-btn hv-btn-primary" onClick={savePortfolio} disabled={portfolioSaving}>
              {portfolioSaving ? 'Saving...' : editingPortfolioId ? 'Save Changes' : 'Create Portfolio'}
            </button>
            {editingPortfolioId && (
              <button className="hv-btn hv-btn-ghost" onClick={resetPortfolioForm}>
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {showForm && (
        <div className="hv-card" style={{ padding: '24px', marginBottom: '24px' }}>
          <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--hv-text-primary)', marginBottom: '6px' }}>
            {editingProjectId ? 'Edit Project' : 'New Project'}
          </div>

          <p style={{ fontSize: '13px', color: 'var(--hv-text-muted)', marginBottom: '20px' }}>
            Projects are master entities like Annual Partnership Programme 2026 or Regional Event Series. Once created, deals are allocated to them with a budget amount.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="hv-form-label">Project Name <span style={{ color: 'var(--hv-red)' }}>*</span></label>
              <input
                className="hv-input"
                placeholder="e.g. Annual Partnership Programme 2026"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
              />
            </div>

            {businessUnits.length > 1 && <div>
              <label className="hv-form-label">Business <span style={{ color: 'var(--hv-red)' }}>*</span></label>
              <select className="hv-select" value={form.business_unit_id} onChange={e => setForm({ ...form, business_unit_id: e.target.value, portfolio_id: '' })}>
                <option value="">Select Business</option>
                {businessUnits.map(unit => <option key={unit.id} value={unit.id}>{unit.name}</option>)}
              </select>
            </div>}

            <div>
              <label className="hv-form-label">Portfolio</label>
              <select className="hv-select" value={form.portfolio_id} onChange={e => setForm({ ...form, portfolio_id: e.target.value })}>
                <option value="">Unassigned</option>
                {portfolios
                  .filter(portfolio => portfolio.status !== 'archived' && portfolio.business_unit_id === form.business_unit_id)
                  .map(portfolio => (
                    <option key={portfolio.id} value={portfolio.id}>{portfolio.name}</option>
                  ))}
              </select>
            </div>

            <div>
              <label className="hv-form-label">Status</label>
              <select className="hv-select" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                {['active', 'draft', 'completed', 'archived'].map(s => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="hv-form-label">Assign To</label>
              <select className="hv-select" value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })}>
                <option value="">Unassigned</option>
                {profiles.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.full_name || p.email}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="hv-form-label">Start Date</label>
              <input
                className="hv-input"
                type="date"
                value={form.start_date}
                onChange={e => setForm({ ...form, start_date: e.target.value })}
              />
            </div>

            <div>
              <label className="hv-form-label">End Date</label>
              <input
                className="hv-input"
                type="date"
                value={form.end_date}
                onChange={e => setForm({ ...form, end_date: e.target.value })}
              />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label className="hv-form-label">Notes</label>
              <input
                className="hv-input"
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button className="hv-btn hv-btn-primary" onClick={saveProject} disabled={saving}>
              {saving ? 'Saving...' : editingProjectId ? 'Save Project' : 'Create Project'}
            </button>

            {editingProjectId && (
              <button className="hv-btn hv-btn-ghost" onClick={resetProjectForm}>
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {canViewFinancials && <div className="projects-reporting-bar"><label htmlFor="projects-reporting-currency">Reporting currency</label><select id="projects-reporting-currency" className="hv-select" value={reportingCurrency} onChange={event => changeReportingCurrency(event.target.value)}>{REPORTING_CURRENCIES.map(currency => <option key={currency}>{currency}</option>)}</select></div>}

      <div className="summary-strip">
        <SummaryItem value={projectCards.length} label="Total Projects" icon={<FolderKanban size={21} />} tone="indigo" />
        {canViewFinancials && <SummaryItem value={formatReportingRevenue(totalRevenueByCurrency, reportingCurrency, rateSnapshot)} label="Total Revenue" icon={<CircleDollarSign size={21} />} tone="green" />}
        <SummaryItem value={totalActiveDeals} label="Active Deals" icon={<BriefcaseBusiness size={21} />} tone="blue" />
        <SummaryItem
          value={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              <span
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  background: `conic-gradient(#6366F1 ${avgHealth * 3.6}deg, #EEF2F7 0deg)`,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <span style={{ width: 24, height: 24, borderRadius: '50%', background: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800 }}>
                  {avgHealth}
                </span>
              </span>
              <span>{avgHealth}%</span>
            </span>
          }
          label="Delivery Completion"
          icon={<Gauge size={21} />}
          tone="amber"
        />
      </div>

      {canViewFinancials && rateSnapshot?.rate_date && <div className="projects-rate-note">Converted using ECB reference rates from {new Date(`${rateSnapshot.rate_date}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}.</div>}

      {error && (
        <div style={{
          background: 'var(--hv-red-bg)',
          border: '1px solid #fca5a5',
          borderRadius: 'var(--hv-radius)',
          padding: '14px 18px',
          color: 'var(--hv-red-text)',
          fontSize: '13.5px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--hv-text-muted)', fontSize: '13.5px', padding: '40px 0' }}>
          Loading...
        </div>
      ) : portfolioGroups.length === 0 ? (
        <div className="hv-table-wrap">
          <div className="hv-empty">
            <div className="hv-empty-title">{search ? 'No projects match your search' : 'No portfolios or projects yet'}</div>
            <div className="hv-empty-text">Create a portfolio or project using the buttons above.</div>
          </div>
        </div>
      ) : (
        <div className="portfolio-stack">
          {portfolioGroups.map(group => {
            const collapsed = collapsedPortfolios[group.portfolio.id]
            const isDropTarget = dragOverPortfolioId === group.portfolio.id

            return (
              <section
                className={`portfolio-section${isDropTarget ? ' portfolio-section--drop-target' : ''}`}
                key={group.portfolio.id}
                onDragOver={event => {
                  event.preventDefault()
                  if (dragOverPortfolioId !== group.portfolio.id) {
                    setDragOverPortfolioId(group.portfolio.id)
                  }
                }}
                onDragLeave={event => {
                  if (!event.currentTarget.contains(event.relatedTarget)) {
                    setDragOverPortfolioId(null)
                  }
                }}
                onDrop={event => handleProjectDrop(event, group.portfolio.id)}
              >
                <div className="portfolio-section-header">
                  <button
                    type="button"
                    onClick={() => togglePortfolio(group.portfolio.id)}
                    style={{ border: 'none', background: 'transparent', padding: 0, display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1, cursor: 'pointer' }}
                  >
                    {collapsed ? <ChevronRight size={17} color="var(--hv-text-muted)" /> : <ChevronDown size={17} color="var(--hv-text-muted)" />}
                    <div className="portfolio-title-block">
                      <div className="portfolio-icon">
                        <FolderKanban size={17} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div className="portfolio-name">{group.portfolio.name}</div>
                        <div className="portfolio-description">{group.portfolio.description || (group.portfolio.system ? 'Projects not assigned to a portfolio.' : 'Portfolio reporting group')}</div>
                      </div>
                    </div>
                  </button>

                  <div className="portfolio-metrics">
                    <span className="portfolio-badge">{group.projectCount} {group.projectCount === 1 ? 'Project' : 'Projects'}</span>
                    <span className="portfolio-badge">{group.revenueDisplay}</span>
                    <span className="portfolio-badge">{group.completion}% Complete</span>

                    <div className="portfolio-actions">
                      <button
                        className="hv-btn portfolio-action-icon"
                        title={group.portfolio.system ? 'Unassigned actions' : 'Portfolio actions'}
                        data-project-menu
                        onClick={e => {
                          e.stopPropagation()
                          setActivePortfolioMenuId(prev => prev === group.portfolio.id ? null : group.portfolio.id)
                        }}
                      >
                        <MoreHorizontal size={15} />
                      </button>

                      {activePortfolioMenuId === group.portfolio.id && (
                        <div className="portfolio-menu" data-project-menu>
                          {group.portfolio.system ? (
                            <button
                              className="portfolio-menu-item"
                              onClick={() => {
                                startNewPortfolio()
                                setActivePortfolioMenuId(null)
                              }}
                            >
                              <Plus size={14} />
                              New portfolio
                            </button>
                          ) : (
                            <>
                              <button
                                className="portfolio-menu-item"
                                onClick={() => {
                                  editPortfolio(group.portfolio)
                                  setActivePortfolioMenuId(null)
                                }}
                              >
                                <Pencil size={14} />
                                Edit portfolio
                              </button>

                              <button
                                className="portfolio-menu-item"
                                onClick={() => {
                                  navigate(`/portfolios/${group.portfolio.id}`)
                                  setActivePortfolioMenuId(null)
                                }}
                              >
                                <BarChart3 size={14} />
                                Open dashboard
                              </button>

                              <button
                                className="portfolio-menu-item"
                                onClick={() => {
                                  updatePortfolioStatus(
                                    group.portfolio.id,
                                    group.portfolio.status === 'archived' ? 'active' : 'archived'
                                  )
                                  setActivePortfolioMenuId(null)
                                }}
                              >
                                {group.portfolio.status === 'archived' ? <RotateCcw size={14} /> : <Archive size={14} />}
                                {group.portfolio.status === 'archived' ? 'Restore portfolio' : 'Archive portfolio'}
                              </button>

                              <button
                                className="portfolio-menu-item portfolio-menu-item--danger"
                                onClick={() => {
                                  setConfirmPortfolioDelete(group.portfolio.id)
                                  setActivePortfolioMenuId(null)
                                }}
                              >
                                <Trash2 size={14} />
                                Delete portfolio
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {!collapsed && (
                  <div className="portfolio-body">
                    {group.projects.length === 0 ? (
                      <div className="portfolio-empty">No projects in this portfolio.</div>
                    ) : (
                      <div className="projects-grid">
                        {group.projects.map(project => (
                          <ProjectCard
                            key={project.id}
                            project={project}
                            isDragging={draggingProjectId === project.id}
                            onDragStart={event => {
                              event.dataTransfer.effectAllowed = 'move'
                              event.dataTransfer.setData('text/plain', project.id)
                              setDraggingProjectId(project.id)
                              setActiveProjectMenuId(null)
                            }}
                            onDragEnd={() => {
                              setDraggingProjectId(null)
                              setDragOverPortfolioId(null)
                            }}
                            onOpen={() => navigate(`/projects/${project.id}`)}
                            onEdit={() => editProject(project)}
                            onDelete={() => setConfirmDelete(project.id)}
                            menuOpen={activeProjectMenuId === project.id}
                            onToggleMenu={() => setActiveProjectMenuId(prev => prev === project.id ? null : project.id)}
                            onCloseMenu={() => setActiveProjectMenuId(null)}
                            canViewFinancials={canViewFinancials}
                            canManageProjects={canManageProjects}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </section>
            )
          })}
        </div>
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Delete project"
          message={`"${projectToDelete?.name}" will be permanently removed along with allocations and rights under it. Linked deals will be retained and unlinked from this project.`}
          confirmLabel="Delete Project"
          onConfirm={() => deleteProject(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {confirmPortfolioDelete && (
        <ConfirmModal
          title="Delete portfolio"
          message={`"${portfolioToDelete?.name}" will be permanently removed. Only empty portfolios can be deleted.`}
          confirmLabel="Delete Portfolio"
          onConfirm={() => deletePortfolio(confirmPortfolioDelete)}
          onCancel={() => setConfirmPortfolioDelete(null)}
        />
      )}
    </div>
  )
}

function buildPortfolioGroup(portfolio, projects, reportingCurrency, rateSnapshot) {
  const revenueByCurrency = projects.reduce((totals, project) => {
    Object.entries(project.revenueByCurrency || {}).forEach(([currency, amount]) => {
      totals[currency] = (totals[currency] || 0) + Number(amount || 0)
    })

    return totals
  }, {})
  const convertedRevenue = getReportingRevenue(revenueByCurrency, reportingCurrency, rateSnapshot)
  const revenue = convertedRevenue === null ? getPrimaryRevenueValue(revenueByCurrency) : convertedRevenue
  const totalDeliveries = projects.reduce((sum, project) => sum + project.deliveryCount, 0)
  const completedDeliveries = projects.reduce((sum, project) => sum + project.completedDeliveries, 0)
  const completion = totalDeliveries > 0
    ? Math.round((completedDeliveries / totalDeliveries) * 100)
    : 0

  return {
    portfolio,
    projects,
    projectCount: projects.length,
    revenue,
    revenueByCurrency,
    revenueDisplay: formatReportingRevenue(revenueByCurrency, reportingCurrency, rateSnapshot),
    completion
  }
}

function SummaryItem({ value, label, icon, tone }) {
  return (
    <div className="summary-item">
      <div className={`summary-icon ${tone}`}>{icon}</div>
      <div className="summary-copy"><div className="summary-value">{value}</div><div className="summary-label">{label}</div></div>
      <ChevronRight className="summary-arrow" size={16} />
    </div>
  )
}

function ProjectCard({
  project,
  isDragging,
  onDragStart,
  onDragEnd,
  onOpen,
  onEdit,
  onDelete,
  menuOpen,
  onToggleMenu,
  onCloseMenu,
  canViewFinancials,
  canManageProjects
}) {
  return (
    <div
      className={`project-card${isDragging ? ' project-card--dragging' : ''}`}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
    >
      <div className="project-card-body">
        <div className="project-card-title-row">
          <div>
            <div className="project-name">{project.name}</div>
            <div className="project-date">
              {project.start_date ? formatDate(project.start_date) : formatStatus(project.status)}
            </div>
          </div>

          <button
            className="hv-btn hv-btn-ghost"
            style={{ padding: 4, color: 'var(--hv-text-muted)' }}
            data-project-menu
            onClick={e => {
              e.stopPropagation()
              onToggleMenu()
            }}
            title="Project actions"
          >
            <MoreHorizontal size={17} />
          </button>

          {menuOpen && (
            <div
              className="portfolio-menu"
              data-project-menu
              style={{ top: 28, right: 0 }}
              onClick={e => e.stopPropagation()}
            >
              {canManageProjects && <button
                className="portfolio-menu-item"
                onClick={() => {
                  onCloseMenu()
                  onOpen()
                }}
              >
                <ChevronRight size={14} />
                View project
              </button>}

              {canManageProjects && <button
                className="portfolio-menu-item"
                onClick={() => {
                  onCloseMenu()
                  onEdit()
                }}
              >
                <Pencil size={14} />
                Edit project
              </button>}

              <button
                className="portfolio-menu-item portfolio-menu-item--danger"
                onClick={() => {
                  onCloseMenu()
                  onDelete()
                }}
              >
                <Trash2 size={14} />
                Delete project
              </button>
            </div>
          )}
        </div>

        <div className="project-card-mid">
          <div>
            {canViewFinancials && <>
              <div className="project-revenue">{project.revenueDisplay}</div>
              <div className="project-mini-label">Revenue</div>
            </>}

            <div style={{ marginTop: 18, fontSize: 18, fontWeight: 800, color: 'var(--hv-text-primary)' }}>
              {project.dealCount}
            </div>
            <div className="project-mini-label">
              {project.dealCount === 1 ? 'Deal' : 'Deals'}
            </div>

            <div style={{ marginTop: 10, fontSize: 18, fontWeight: 800, color: 'var(--hv-text-primary)' }}>
              {project.completedDeliveries}/{project.deliveryCount}
            </div>
            <div className="project-mini-label">Deliveries</div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <div
              className="health-ring-small"
              style={{
                background: `conic-gradient(#6366F1 ${project.healthScore * 3.6}deg, #EEF2F7 0deg)`
              }}
            >
              <div className="health-ring-inner-small" style={{ color: '#6366F1' }}>
                {project.healthScore}
              </div>
            </div>

            <div style={{ fontSize: 11, color: '#6366F1', fontWeight: 700, marginTop: 5 }}>
              {project.progressLabel}
            </div>

            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: 8,
                padding: '4px 8px',
                borderRadius: 999,
                fontSize: 10,
                fontWeight: 800,
                color: project.statusBadge.color,
                background: project.statusBadge.background,
                whiteSpace: 'nowrap'
              }}
            >
              {project.statusBadge.label}
            </div>
          </div>
        </div>
      </div>

      <div className="project-card-footer">
        <span>Next deadline</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {project.nextDeadline ? formatDate(project.nextDeadline) : '—'}
          <CalendarDays size={13} />
        </span>
      </div>
    </div>
  )
}
